import { useEffect, useRef, useState } from 'react'
import { triageAPI } from '../../services/triageAPI'
import { humanSessionAPI } from '../../services/humanSessionAPI'
import { useVoiceDictation } from '../../hooks/useVoiceDictation'
import TriageReadout, { confidenceColor } from './TriageReadout'

const DIM_LABELS = {
  routing: 'Correct routing',
  question_quality: 'Question quality',
  red_flag_capture: 'Red-flag capture',
  missing_data: 'Missing-data ID',
}

function ScoreBars({ scores, title }) {
  if (!scores) return null
  return (
    <div>
      {title && <div className="text-xs uppercase tracking-wide text-base-content/60 mb-2">{title}</div>}
      <div className="space-y-1.5">
        {Object.keys(DIM_LABELS).map((k) => {
          const v = scores[k]
          const pct = v == null ? 0 : Math.round(v * 100)
          return (
            <div key={k} className="text-xs">
              <div className="flex justify-between">
                <span>{DIM_LABELS[k]}</span>
                <span className="font-mono text-base-content/60">{v == null ? '—' : v.toFixed(2)}</span>
              </div>
              <div className="h-1.5 bg-base-200 rounded mt-0.5 overflow-hidden">
                <div className={`h-full ${confidenceColor(v)}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── voice/text input for the trainee's questions to the patient ──────────────

function ClinicianInput({ onAsk, disabled, useWhisper }) {
  const { isSupported, isListening, transcript, setTranscript, interimText, startListening, stopListening, reset } =
    useVoiceDictation({ useWhisper, whisperUploadFn: useWhisper ? humanSessionAPI.transcribeAudio : null })

  const ask = () => {
    const text = transcript.trim()
    if (!text) return
    onAsk(text)
    reset()
  }

  return (
    <div className="space-y-2">
      <textarea
        className="textarea textarea-bordered w-full text-sm resize-none"
        rows={2}
        placeholder="Ask the patient a question…"
        value={transcript + (interimText ? ' ' + interimText : '')}
        onChange={(e) => setTranscript(e.target.value)}
        disabled={disabled}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask() } }}
      />
      <div className="flex gap-2 items-center">
        {isSupported && (
          <button
            className={`btn btn-sm ${isListening ? 'btn-error' : 'btn-ghost border border-base-300'}`}
            onClick={() => (isListening ? stopListening() : startListening())}
            disabled={disabled}
          >
            {isListening ? <span className="flex items-center gap-1"><span className="loading loading-ring loading-xs" />Stop</span>
              : transcript.trim() ? '🎤 Add more' : '🎤 Speak'}
          </button>
        )}
        <button className="btn btn-primary btn-sm" onClick={ask} disabled={disabled || !transcript.trim()}>Ask →</button>
      </div>
    </div>
  )
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function TriageTraining({ caseInfo, buckets = {}, providers = ['openai'], useWhisper = false }) {
  const caseId = caseInfo?.id

  // Agentic run
  const [agentic, setAgentic] = useState(null)
  const [agenticLoading, setAgenticLoading] = useState(false)
  const [showAgentTranscript, setShowAgentTranscript] = useState(false)

  // Human session
  const [sessionId, setSessionId] = useState(null)
  const [turns, setTurns] = useState([])              // {speaker: 'clinician'|'patient', text}
  const [busy, setBusy] = useState(false)
  const [assessmentText, setAssessmentText] = useState('')
  const [submitResult, setSubmitResult] = useState(null)

  const [error, setError] = useState(null)
  const scrollRef = useRef(null)

  // Reset everything when the selected case changes.
  useEffect(() => {
    setAgentic(null); setShowAgentTranscript(false)
    setSessionId(null); setTurns([]); setAssessmentText(''); setSubmitResult(null); setError(null)
  }, [caseId])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns, busy])

  const runAgent = async () => {
    setAgenticLoading(true); setError(null)
    try {
      const d = await triageAPI.agenticRun({ caseId, providers, maxTurns: 6 })
      setAgentic(d)
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setAgenticLoading(false)
    }
  }

  const startHuman = async () => {
    setBusy(true); setError(null); setSubmitResult(null); setTurns([])
    try {
      const d = await triageAPI.humanStart({ caseId })
      setSessionId(d.session_id)
      setTurns([{ speaker: 'patient', text: d.chief_complaint }])
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setBusy(false)
    }
  }

  const ask = async (text) => {
    setTurns((t) => [...t, { speaker: 'clinician', text }])
    setBusy(true); setError(null)
    try {
      const d = await triageAPI.humanReply(sessionId, text)
      setTurns((t) => [...t, { speaker: 'patient', text: d.patient_text }])
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    if (!assessmentText.trim()) return
    setBusy(true); setError(null)
    try {
      const d = await triageAPI.humanSubmit(sessionId, { assessmentText, providers })
      setSubmitResult(d)
    } catch (e) {
      setError(e?.response?.data?.detail || e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!caseInfo) return null
  const comparison = submitResult?.comparison
  const agentAssessment = agentic?.assessment

  return (
    <div className="space-y-4">
      {/* Case header */}
      <div className="card bg-base-100 border border-base-300 p-4">
        <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono">Triage training · {caseInfo.id}</div>
        <h2 className="text-xl font-bold mt-1">{caseInfo.label}</h2>
        <p className="text-sm text-base-content/70 mt-1">{caseInfo.one_liner}</p>
        <div className="text-xs text-base-content/60 mt-2">
          Interview the simulated patient, then compare your triage with the agent. Ground truth is hidden until you submit.
        </div>
      </div>

      {error && <div className="alert alert-error text-sm">{String(error)}</div>}

      {/* ── Agentic triage ── */}
      <div className="card bg-base-100 border border-primary/30 p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h3 className="font-semibold text-primary">🤖 Agentic triage</h3>
          <button className={`btn btn-primary btn-sm ${agenticLoading ? 'btn-disabled' : ''}`} onClick={runAgent} disabled={agenticLoading}>
            {agenticLoading && <span className="loading loading-spinner loading-xs" />}
            {agenticLoading ? 'Interviewing patient…' : agentic ? 'Re-run agent' : 'Run agent'}
          </button>
        </div>
        {!agentic && !agenticLoading && (
          <p className="text-sm text-base-content/60">The triage agent interviews the simulated patient automatically, then routes and scores itself.</p>
        )}
        {agentic && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
            <div>
              <button className="btn btn-ghost btn-xs" onClick={() => setShowAgentTranscript((s) => !s)}>
                {showAgentTranscript ? 'Hide' : 'Show'} agent interview ({agentic.interview.questions_asked.length} questions)
              </button>
              {showAgentTranscript && (
                <div className="mt-2 space-y-2 max-h-72 overflow-y-auto border-t border-base-200 pt-2">
                  {agentic.interview.transcript.map((t, i) => (
                    <div key={i} className={`text-sm px-3 py-1.5 rounded ${t.role === 'patient' ? 'bg-base-200' : 'bg-primary/8'}`}>
                      <span className="text-xs uppercase font-mono text-base-content/60 mr-2">{t.role === 'patient' ? 'PT' : 'Agent'}</span>
                      {t.text}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3"><ScoreBars scores={agentic.score?.scores} title="Agent score vs ground truth" /></div>
            </div>
            <div className="border-l border-base-200 pl-4">
              <TriageReadout assessment={agentAssessment} buckets={buckets} handoff={agentic.interview?.handoff} />
            </div>
          </div>
        )}
      </div>

      {/* ── Your turn ── */}
      <div className="card bg-base-100 border border-base-300 p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h3 className="font-semibold">🩺 Your turn</h3>
          {!sessionId && (
            <button className="btn btn-outline btn-primary btn-sm" onClick={startHuman} disabled={busy}>
              {busy && <span className="loading loading-spinner loading-xs" />} Interview the patient
            </button>
          )}
        </div>

        {sessionId && (
          <>
            <div ref={scrollRef} className="space-y-2 max-h-[40vh] overflow-y-auto mb-3">
              {turns.map((t, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded ${t.speaker === 'patient' ? 'bg-base-200' : 'bg-primary/8 border border-primary/20'}`}>
                  <div className="text-xs uppercase tracking-wide text-base-content/60 font-mono mb-1">{t.speaker === 'patient' ? 'Patient' : 'You'}</div>
                  <div className="whitespace-pre-wrap">{t.text}</div>
                </div>
              ))}
              {busy && <div className="flex items-center gap-2 text-sm text-base-content/60"><span className="loading loading-dots loading-sm" /> …</div>}
            </div>

            {!submitResult && (
              <>
                <ClinicianInput onAsk={ask} disabled={busy} useWhisper={useWhisper} />
                <div className="divider text-xs text-base-content/50 my-2">When ready, submit your triage assessment</div>
                <textarea
                  className="textarea textarea-bordered w-full text-sm resize-none"
                  rows={3}
                  placeholder="Your assessment: suspected category, your reasoning, and what data you'd gather next…"
                  value={assessmentText}
                  onChange={(e) => setAssessmentText(e.target.value)}
                  disabled={busy}
                />
                <button className="btn btn-primary btn-sm mt-2" onClick={submit} disabled={busy || !assessmentText.trim()}>
                  Submit assessment & compare
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* ── Comparison + coaching ── */}
      {submitResult && (
        <div className="card bg-base-100 border border-success/40 p-4">
          <h3 className="font-semibold text-success mb-3">📊 Side-by-side: you vs the agent</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded border border-primary/20 p-3">
              <ScoreBars scores={submitResult.score?.scores} title="Your score" />
              <div className="text-xs mt-2 text-base-content/70">
                You routed to: <span className="font-mono">{submitResult.score?.detected_bucket || '—'}</span>{' '}
                {submitResult.score?.routing_correct ? <span className="text-success">✓ correct</span> : <span className="text-error">✗ vs {submitResult.score?.expected_bucket}</span>}
              </div>
            </div>
            <div className="rounded border border-base-300 p-3">
              <ScoreBars scores={submitResult.agentic_score?.scores} title="Agent score" />
              {comparison?.agent_bucket && (
                <div className="text-xs mt-2 text-base-content/70">Agent routed to: <span className="font-mono">{comparison.agent_bucket}</span></div>
              )}
            </div>
          </div>

          {comparison && (
            <div className="mt-4 space-y-3">
              {comparison.summary && <div className="alert py-2 text-sm"><span>{comparison.summary}</span></div>}
              {comparison.routing_delta && (
                <div className="text-sm"><span className="font-semibold">Routing:</span> {comparison.routing_delta}</div>
              )}
              {comparison.missed_questions?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">High-yield questions you missed</div>
                  <ul className="list-disc list-inside text-sm space-y-0.5">{comparison.missed_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
              {comparison.missed_red_flags?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-warning mb-1">Safety signals you missed</div>
                  <ul className="list-disc list-inside text-sm space-y-0.5">{comparison.missed_red_flags.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
              {comparison.gaps?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Where to improve</div>
                  <ul className="list-disc list-inside text-sm space-y-0.5">{comparison.gaps.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
              {comparison.strengths?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-success mb-1">Strengths</div>
                  <ul className="list-disc list-inside text-sm space-y-0.5">{comparison.strengths.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          {!comparison && (
            <p className="text-xs text-base-content/60 mt-3">Run the agent above to unlock side-by-side coaching for this case.</p>
          )}
        </div>
      )}
    </div>
  )
}
